import React, { useState, useEffect, useRef } from "react";
import { IconBack, IconNoListing } from "../components/Icons";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useApp } from "../App";

function ImageViewer({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const lastTap = useRef(0);
  const lastDist = useRef(null);
  const lastOffset = useRef({ x: 0, y: 0 });
  const startOffset = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);

  const clampOffset = (x, y, s) => {
    if (!imgRef.current) return { x, y };
    const rect = imgRef.current.getBoundingClientRect();
    const maxX = Math.max(0, (rect.width * s - window.innerWidth) / 2);
    const maxY = Math.max(0, (rect.height * s - window.innerHeight) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      lastDist.current = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    } else if (e.touches.length === 1) {
      startOffset.current = {
        x: e.touches[0].clientX - lastOffset.current.x,
        y: e.touches[0].clientY - lastOffset.current.y,
      };
      const now = Date.now();
      if (now - lastTap.current < 300) {
        // double tap
        if (scale > 1) {
          setScale(1);
          setOffset({ x: 0, y: 0 });
          lastOffset.current = { x: 0, y: 0 };
        } else {
          setScale(2.5);
        }
      }
      lastTap.current = now;
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (lastDist.current) {
        const delta = dist / lastDist.current;
        setScale(s => {
          const next = Math.min(5, Math.max(1, s * delta));
          return next;
        });
      }
      lastDist.current = dist;
    } else if (e.touches.length === 1 && scale > 1) {
      const x = e.touches[0].clientX - startOffset.current.x;
      const y = e.touches[0].clientY - startOffset.current.y;
      const clamped = clampOffset(x, y, scale);
      setOffset(clamped);
      lastOffset.current = clamped;
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) lastDist.current = null;
    if (scale <= 1) {
      setOffset({ x: 0, y: 0 });
      lastOffset.current = { x: 0, y: 0 };
    }
  };

  return (
    <div
      onClick={() => scale === 1 && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)",
        zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center",
        touchAction: "none",
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: "calc(16px + env(safe-area-inset-top))", right: 16,
          background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
          width: 36, height: 36, borderRadius: "50%", fontSize: 18,
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 10,
        }}
      >×</button>
      <div
        style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", position: "absolute", bottom: "calc(20px + env(safe-area-inset-bottom))", left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}
      >
        Pinch to zoom · Double tap to reset
      </div>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          maxWidth: "100vw", maxHeight: "100vh",
          objectFit: "contain",
          transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
          transition: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
        draggable={false}
      />
    </div>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, user } = useApp();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [editing, setEditing] = useState(false);
  const [editPrice, setEditPrice] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "listings", id)).then(d => {
      if (d.exists()) {
        const data = { id: d.id, ...d.data() };
        setListing(data);
        setEditPrice(String(data.price));
        setEditNote(data.note || "");
      }
      setLoading(false);
    });
  }, [id]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hi! I'm interested in your ${listing.itemName} (Size ${listing.size}) for €${listing.price} listed on kepti.`
    );
    const number = (listing.userWhatsApp || "").replace(/\D/g, "");
    window.open(`https://wa.me/${number}?text=${msg}`, "_blank");
  };

  const handleMarkSold = async () => {
    await updateDoc(doc(db, "listings", id), { status: "sold" });
    setListing(l => ({ ...l, status: "sold" }));
    showToast("Marked as sold ✓");
  };

  const handleDelete = async () => {
    if (window.confirm("Delete this listing?")) {
      await deleteDoc(doc(db, "listings", id));
      navigate("/");
    }
  };

  const handleFlag = async () => {
    await updateDoc(doc(db, "listings", id), { flagged: true });
    showToast(t.flagged);
  };

  const handleSaveEdit = async () => {
    if (!editPrice) return;
    setSaving(true);
    await updateDoc(doc(db, "listings", id), {
      price: Number(editPrice),
      note: editNote,
    });
    setListing(l => ({ ...l, price: Number(editPrice), note: editNote }));
    setSaving(false);
    setEditing(false);
    showToast("Listing updated ✓");
  };

  const timeAgo = (ts) => {
    if (!ts) return "";
    const diff = Date.now() - ts.toMillis();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return "<1h " + t.ago;
    if (h < 24) return `${h}h ${t.ago}`;
    return `${Math.floor(h / 24)}d ${t.ago}`;
  };

  const initials = (name) => name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";

  const conditionLabel = (c) => {
    if (c === "likeNew") return t.likeNew;
    if (c === "good") return t.good;
    return t.used;
  };

  const paymentLabel = (p) => {
    if (p === "mbway") return "MBWay";
    if (p === "cash") return t.cash;
    return "MBWay + " + t.cash;
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div className="spinner" style={{ borderTopColor: "var(--navy)" }} />
    </div>
  );

  if (!listing) return (
    <div className="empty-state">
      <div className="empty-icon">🔍</div>
      <p>Listing not found</p>
    </div>
  );

  const isOwner = user?.uid === listing.userId;

  return (
    <div className="listing-detail">
      {lightbox && listing.imageUrl && (
        <ImageViewer
          src={listing.imageUrl}
          alt={listing.itemName}
          onClose={() => setLightbox(false)}
        />
      )}

      <div className="detail-img-wrap">
        <button
          className="back-btn"
          onClick={() => navigate(-1)}
          style={{
            position: "absolute",
            top: "calc(12px + env(safe-area-inset-top))",
            left: 12,
            width: 36, height: 36,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.9)",
            border: "none", fontSize: 18, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.15)"
          }}
        ><IconBack /></button>

        <div
          className="detail-img"
          onClick={() => listing.imageUrl && setLightbox(true)}
          style={{ cursor: listing.imageUrl ? "zoom-in" : "default" }}
        >
          {listing.imageUrl
            ? <img src={listing.imageUrl} alt={listing.itemName} />
            : <IconNoListing />
          }
          {listing.imageUrl && (
            <div style={{
              position: "absolute", bottom: 8, right: 8,
              background: "rgba(0,0,0,0.4)", borderRadius: 6,
              padding: "3px 8px", fontSize: 11, color: "#fff",
            }}>
              Tap to zoom
            </div>
          )}
        </div>
      </div>

      <div className="detail-body">
        {listing.status === "sold" && (
          <div style={{ background: "#fdf3e7", color: "#7a4a0a", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
            SOLD
          </div>
        )}

        <h1 className="detail-title">{listing.itemName}</h1>

        {editing ? (
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Price (€)</label>
            <input
              className="form-input"
              type="number"
              value={editPrice}
              onChange={e => setEditPrice(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <label className="form-label">Note</label>
            <textarea
              className="form-textarea"
              value={editNote}
              onChange={e => setEditNote(e.target.value)}
              placeholder="e.g. small mark on sleeve, zipper works fine"
              style={{ marginBottom: 12 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-primary" onClick={handleSaveEdit} disabled={saving} style={{ flex: 1 }}>
                {saving ? "Saving..." : "Save changes"}
              </button>
              <button className="btn-secondary" onClick={() => setEditing(false)} style={{ flex: 1 }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="detail-price">€{listing.price}</div>
            <div className="detail-pills">
              <span className="detail-pill">Size {listing.size}</span>
              <span className="detail-pill">{conditionLabel(listing.condition)}</span>
              <span className="detail-pill">{paymentLabel(listing.payment)}</span>
              {listing.category && <span className="detail-pill">{t[listing.category] || listing.category}</span>}
            </div>
            {listing.note && (
              <p className="detail-note">"{listing.note}"</p>
            )}
          </>
        )}

        <div className="detail-divider" />

        <div className="detail-seller">
          <div className="seller-avatar">{initials(listing.userName)}</div>
          <div>
            <div className="seller-name">{listing.userName}</div>
            <div className="seller-time">{t.postedBy} · {timeAgo(listing.createdAt)}</div>
          </div>
        </div>

        <div className="detail-divider" />

        <div className="detail-actions">
          {!isOwner && listing.status === "active" && (
            <>
              <div style={{
                background: "var(--offwhite)", borderRadius: 10,
                padding: "12px 14px", fontSize: 13,
                color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 4
              }}>
                Payment agreed between buyer and seller via WhatsApp.<br/>
                kepti doesn't process payments.<br/>
                Tap "Contact seller" to arrange payment and collection.
              </div>
              <button className="btn-primary" onClick={handleWhatsApp}>
                {t.contactSeller}
              </button>
            </>
          )}

          {isOwner && listing.status === "active" && (
            <>
              <button className="btn-primary" onClick={() => setEditing(true)}>
                Edit price or note
              </button>
              <button className="btn-secondary" onClick={handleMarkSold}>{t.markSold}</button>
              <button className="btn-danger" onClick={handleDelete}>{t.deleteListing}</button>
            </>
          )}

          {!isOwner && (
            <button
              onClick={handleFlag}
              style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 13, cursor: "pointer", padding: "8px 0", fontFamily: "inherit" }}
            >
              {t.flag}
            </button>
          )}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
